import {
  subclass,
  property,
} from "@arcgis/core/core/accessorSupport/decorators";

import Widget from "@arcgis/core/widgets/Widget";

import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import { VNode } from "src/interfaces/interfaces";

import "./FeaturedSidebar.scss";

import AppT9N from "../../../t9n/App/App.json";
import NavBarViewModel from "src/app/ui/NavBar/NavBarViewModel";

import SidebarTilePic from "../../../../public/assets/featuredArticle/articleTilePics/SidebarArticle.png";
import ChartViewerTilePic from "../../../../public/assets/featuredArticle/articleTilePics/ChartViewerArticle.png";
import ThemingTilePic from "../../../../public/assets/featuredArticle/articleTilePics/ThemingArticle.jpg";
import InsetsTilePic from "../../../../public/assets/featuredArticle/articleTilePics/Insets_Article.jpg";
import AtlasTilePic from "../../../../public/assets/featuredArticle/articleTilePics/AtlasArticle.png";
import ManagerTilePic from "../../../../public/assets/featuredArticle/articleTilePics/ManagerArticle.png";
import ReporterTilePic from "../../../../public/assets/featuredArticle/articleTilePics/ReporterArticle.png";
import LanguageSwitcherPic from "../../../../public/assets/featuredArticle/articleTilePics/LanguageSwitcherArticle.png";
import ComparePic from "../../../../public/assets/featuredArticle/articleTilePics/CompareArticle.png";

import NewBlogPic from "../../../../public/assets/featuredArticle/instant-apps-what-is-new.png";
import BlogPic from "../../../../public/assets/featuredArticle/instant-apps-blog.jpg";

import { keyboardAccessibleHelper } from "../../utils/appUtils";
import { substitute } from "@arcgis/core/intl";

interface IBlogTile {
  imgRef: string;
  headerTitle: string;
  descText: string;
  linkUrl: string;
  linkText: string;
}

interface IArticleTile {
  id: string;
  imgRef: string;
  headerTitle: string;
  descText: string;
}

export const PRODUCT_NAME = "ArcGIS Instant Apps";

@subclass("app.widgets.Sidebar.FeaturedSidebar")
export default class FeaturedSidebar extends Widget {
  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppT9N;

  @property()
  navbarVM: NavBarViewModel;

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  constructor(args) {
    super(args);
  }

  render() {
    const { leftSidebarTitle, sidebarTitle } = this.appMessages.featuredTab;
    return (
      <div class="featured-sidebar">
        <calcite-tabs layout="center" scale="l">
          <calcite-tab-nav slot="title-group">
            <calcite-tab-title selected>{leftSidebarTitle}</calcite-tab-title>
            <calcite-tab-title>{sidebarTitle}</calcite-tab-title>
          </calcite-tab-nav>
          <calcite-tab selected>{this._renderArticleSelections()}</calcite-tab>
          <calcite-tab>{this._sidebarRouter()}</calcite-tab>
        </calcite-tabs>
      </div>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private _renderArticleSelections(): VNode {
    const {
      sidebarArticle,
      sidebarArticleDesc,
      chartViewerArticle,
      chartViewerArticleDesc,
      themingArticle,
      themingArticleDesc,
      insetsArticle,
      insetsArticleDesc,
      atlasArticle,
      atlasArticleDesc,
      managerArticle,
      managerArticleDesc,
      reporterArticle,
      reporterArticleDesc,
      langSwitcherArticle,
      langSwitcherArticleDesc,
      compareArticle,
      compareArticleDesc,
    } = this.appMessages.featuredTab;

    return (
      <div>
        {this._renderArticleTile({
          id: "manager",
          imgRef: ManagerTilePic,
          headerTitle: managerArticle,
          descText: managerArticleDesc,
        })}
        {this._renderArticleTile({
          id: "reporter",
          imgRef: ReporterTilePic,
          headerTitle: reporterArticle,
          descText: reporterArticleDesc,
        })}
        {this._renderArticleTile({
          id: "compare",
          imgRef: ComparePic,
          headerTitle: compareArticle,
          descText: compareArticleDesc,
        })}
        {this._renderArticleTile({
          id: "languageSwitcher",
          imgRef: LanguageSwitcherPic,
          headerTitle: langSwitcherArticle,
          descText: langSwitcherArticleDesc,
        })}
        {this._renderArticleTile({
          id: "atlas",
          imgRef: AtlasTilePic,
          headerTitle: atlasArticle,
          descText: atlasArticleDesc,
        })}
        {this._renderArticleTile({
          id: "insets",
          imgRef: InsetsTilePic,
          headerTitle: insetsArticle,
          descText: insetsArticleDesc,
        })}
        {this._renderArticleTile({
          id: "theming",
          imgRef: ThemingTilePic,
          headerTitle: themingArticle,
          descText: themingArticleDesc,
        })}
        {this._renderArticleTile({
          id: "sidebar",
          imgRef: SidebarTilePic,
          headerTitle: sidebarArticle,
          descText: sidebarArticleDesc,
        })}
        {this._renderArticleTile({
          id: "chartViewer",
          imgRef: ChartViewerTilePic,
          headerTitle: chartViewerArticle,
          descText: chartViewerArticleDesc,
        })}
      </div>
    );
  }

  private _renderArticleTile(articleTile: IArticleTile): VNode {
    const { imgRef, headerTitle, descText, id } = articleTile;
    return (
      <div
        key={id}
        class={this.classes(
          "article-tile",
          this.navbarVM._featuredArticleStateRouter === id
            ? "article-tile--selected"
            : null
        )}
        onclick={this._handleCarouselSelection.bind(this, id)}
        onkeydown={(e: KeyboardEvent) => {
          if (e.code === "Space") {
            // stop space from scrolling the sidebar down
            e.preventDefault();
          }

          keyboardAccessibleHelper(
            this._handleCarouselSelection.bind(this, id),
            e
          );
        }}
        tabindex="0"
      >
        <img src={imgRef} alt="" class="article-tile-image" />
        <div class="article-tile-info">
          <header class="article-header-text">{headerTitle}</header>
        </div>
      </div>
    );
  }

  private _sidebarRouter(): VNode {
    const {
      whatsNewText,
      whatsNewDesc,
      whatsNewLink,
      blogText,
      blogDesc,
      blogLink,
    } = this.appMessages.featuredTab;
    return [
      <div class="blog-tiles">
        {this._renderBlogTile({
          imgRef: NewBlogPic,
          headerTitle: substitute(whatsNewText, {
            productName: PRODUCT_NAME,
          }),
          descText: substitute(whatsNewDesc, { productName: PRODUCT_NAME }),
          linkUrl: "https://links.esri.com/agol-help/whats-new-instant-apps",
          linkText: whatsNewLink,
        })}
        {this._renderBlogTile({
          imgRef: BlogPic,
          headerTitle: substitute(blogText, { productName: PRODUCT_NAME }),
          descText: substitute(blogDesc, { productName: PRODUCT_NAME }),
          linkUrl:
            "https://www.esri.com/arcgis-blog/?s=#&products=instant-apps",
          linkText: substitute(blogLink, { productName: PRODUCT_NAME }),
        })}
      </div>,
    ];
  }

  private _renderBlogTile(blogTile: IBlogTile): VNode {
    const { imgRef, headerTitle, descText, linkUrl, linkText } = blogTile;
    return (
      <div class="blog-tile">
        <img src={imgRef} alt="" />
        <header class="blogHeaderText">{headerTitle}</header>
        <p>{descText}</p>
        <calcite-link href={linkUrl} target="_blank" icon-end="launch">
          {linkText}
        </calcite-link>
      </div>
    );
  }

  private _handleCarouselSelection(id: any): void {
    this.navbarVM._featuredArticleStateRouter = id;
  }
}
